/**
 * Run at most `max` tasks at once, queueing the rest in arrival order.
 *
 * For work the browser does not schedule for us. Image loads get a resource
 * scheduler and, on HTTP/1.1, a per-origin connection cap; `fetch` gets
 * neither, and HTTP/2 multiplexes every request onto one connection. So a grid
 * that starts one decode per card runs all of them at once, splits its
 * bandwidth between them, and finishes them together after a long wait rather
 * than progressively.
 */
export function createConcurrencyLimiter(
  max: number,
): <T>(task: () => Promise<T>) => Promise<T> {
  let active = 0;
  const queued: (() => void)[] = [];

  function acquire(): Promise<void> {
    if (active < max) {
      active++;
      return Promise.resolve();
    }
    // The slot is claimed inside the callback, which release() invokes
    // synchronously, so a caller arriving in between cannot take it first.
    return new Promise<void>((resolve) => {
      queued.push(() => {
        active++;
        resolve();
      });
    });
  }

  function release(): void {
    active--;
    queued.shift()?.();
  }

  return async <T>(task: () => Promise<T>): Promise<T> => {
    await acquire();
    try {
      return await task();
    } finally {
      release();
    }
  };
}
